<template>
  <div class="flex flex-col h-full min-h-0 p-4 gap-4">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="heading-2 font-semibold">{{ t('floating-tabs.title') }}</h1>
        <p class="body-3 text-muted-foreground">
          {{ t('floating-tabs.open-tabs', openTabs.length) }}
        </p>
      </div>
      <l-button @click="showNewTab = true" size="small">
        {{ t('floating-tabs.new-tab') }}
      </l-button>
    </div>

    <div class="flex gap-2 overflow-x-auto shrink-0">
      <l-selectable-button
        v-for="filter in filters"
        :key="filter.value"
        size="small"
        :is-selected="selectedFilter === filter.value"
        @click="selectedFilter = filter.value"
      >
        {{ filter.label }}
      </l-selectable-button>
    </div>

    <div class="flex-1 overflow-y-auto">
      <template v-if="selectedFilter === 'all'">
        <div v-for="group in groups" :key="group.status" class="mb-6">
          <div class="flex items-center gap-2 mb-3">
            <div class="w-3 h-3 rounded-full" :class="group.bgColor" />
            <span class="body-3 font-semibold" :class="group.textColor">
              {{ group.label }} ({{ group.tabs.length }})
            </span>
          </div>
          <div class="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
            <floating-tab-card
              v-for="tab in group.tabs"
              :key="tab.id"
              :tab-id="tab.id"
              @click="goToTab(tab.id)"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <div class="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
          <floating-tab-card
            v-for="tab in filteredTabs"
            :key="tab.id"
            :tab-id="tab.id"
            @click="goToTab(tab.id)"
          />
        </div>
      </template>
    </div>

    <new-tab v-if="showNewTab" @close="showNewTab = false" />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton } from '@last/core-ui/paprika'

import LSelectableButton from '@/modules/checkout/LSelectableButton.vue'
import NewTab from '@/modules/tabs/NewTab.vue'
import { useTabsStore } from '@/store/tabs'
import type { Tab } from '@/types'

import FloatingTabCard from './FloatingTabCard.vue'

type FilterValue = 'all' | 'open' | 'billing-started' | 'closed'
type TabStatus = 'open' | 'billing-started' | 'closed'

const { t } = useI18n()
const router = useRouter()

const tabsStore = useTabsStore()
const { tabs } = storeToRefs(tabsStore)

const showNewTab = ref(false)
const selectedFilter = ref<FilterValue>('all')

const floatingTabs = computed(() => {
  return Object.values(tabs.value).filter(
    tab => tab.tables?.length === 0 && tab.source === 'Restaurant'
  )
})

function getTabStatus(tab: Tab): TabStatus {
  if (!tab.open) return 'closed'
  if (tab.billingStartedTime) return 'billing-started'
  return 'open'
}

const openTabs = computed(() => floatingTabs.value.filter(tab => tab.open))

const activeTabs = computed(() => floatingTabs.value.filter(tab => getTabStatus(tab) === 'open'))

const toPayTabs = computed(() =>
  floatingTabs.value.filter(tab => getTabStatus(tab) === 'billing-started')
)

const closedTabs = computed(() => floatingTabs.value.filter(tab => getTabStatus(tab) === 'closed'))

const filters = computed(() => [
  {
    value: 'all' as const,
    label: `${t('floating-tabs.all-open-tabs')} (${openTabs.value.length})`
  },
  { value: 'open' as const, label: `${t('floating-tabs.active')} (${activeTabs.value.length})` },
  {
    value: 'billing-started' as const,
    label: `${t('floating-tabs.to-pay')} (${toPayTabs.value.length})`
  },
  { value: 'closed' as const, label: `${t('floating-tabs.closed')} (${closedTabs.value.length})` }
])

const groups = computed(() => {
  const result: {
    status: TabStatus
    label: string
    bgColor: string
    textColor: string
    tabs: Tab[]
  }[] = []
  if (activeTabs.value.length > 0) {
    result.push({
      status: 'open',
      label: t('floating-tabs.active'),
      bgColor: 'bg-b-500',
      textColor: 'text-b-500',
      tabs: activeTabs.value
    })
  }
  if (toPayTabs.value.length > 0) {
    result.push({
      status: 'billing-started',
      label: t('floating-tabs.to-pay'),
      bgColor: 'bg-r-300',
      textColor: 'text-r-300',
      tabs: toPayTabs.value
    })
  }
  return result
})

const filteredTabs = computed(() => {
  switch (selectedFilter.value) {
    case 'open':
      return activeTabs.value
    case 'billing-started':
      return toPayTabs.value
    case 'closed':
      return closedTabs.value
    default:
      return openTabs.value
  }
})

function goToTab(tabId: string) {
  router.push({ name: 'orderManagement', params: { tabId } })
}
</script>
